/**
 * Signer Role (Issue #1304)
 *
 * Canonical roles for envelope recipients across the Term Sheet e-signature flow
 * and any future multi-signer envelopes. `SigningRecipient.role` is validated
 * against this enum at the app layer; downstream tickets (#1305 resolver API,
 * #1307 admin template builder) consume these values.
 *
 * Legacy values `party_a` / `party_b` are mapped to `borrower` / `lender_signer`
 * by the backfill migration in `34-crm-service/prisma/data-migrations/`.
 */

import { z } from 'zod';

export const SignerRole = {
  /** Primary borrower — always required; signs their own loan documents. */
  BORROWER: 'borrower',
  /** Additional individual borrower on the same loan. */
  CO_BORROWER: 'co_borrower',
  /** Personal guarantor for the borrowing entity. */
  GUARANTOR: 'guarantor',
  /** LLC/Corp managing member or authorized officer signing on behalf of an entity. */
  AUTHORIZED_OFFICER: 'authorized_officer',
  /** Lender-side signer (loan officer, executive) — requires `esign.sign`. */
  LENDER_SIGNER: 'lender_signer',
  /** Credit manager counter-signer (separation-of-duties) — requires `esign.sign`. */
  CREDIT_MANAGER: 'credit_manager',
  /** Non-signing witness. */
  WITNESS: 'witness',
  /** Notary public performing notarization. */
  NOTARY: 'notary',
  /** Carbon-copy recipient — receives a copy, does not sign. */
  CC: 'cc',
} as const;

export type SignerRole = (typeof SignerRole)[keyof typeof SignerRole];

/** Zod schema for validating a SignerRole value. */
export const SignerRoleSchema = z.nativeEnum(SignerRole);

/** Array of all signer roles for iteration (UI dropdowns, validation). */
export const SIGNER_ROLES: readonly SignerRole[] = Object.values(SignerRole);

/**
 * Lender-side roles that require `Permission.esign.sign` on the user.
 * Consumer-side roles (borrower, co_borrower, guarantor, authorized_officer)
 * do not — a borrower can always sign their own documents.
 */
export const LENDER_SIDE_SIGNER_ROLES: readonly SignerRole[] = [SignerRole.LENDER_SIGNER, SignerRole.CREDIT_MANAGER];

/**
 * Consumer-side roles whose signer is resolved from the case's borrowers /
 * guarantors / entity signatory rather than from platform users.
 */
export const CONSUMER_SIDE_SIGNER_ROLES: readonly SignerRole[] = [
  SignerRole.BORROWER,
  SignerRole.CO_BORROWER,
  SignerRole.GUARANTOR,
  SignerRole.AUTHORIZED_OFFICER,
];

/**
 * Legacy role values historically stored in `SigningRecipient.role`.
 * Used by the backfill migration to map to canonical SignerRole values.
 *
 * | Legacy     | Canonical        |
 * |------------|------------------|
 * | party_a    | borrower         |
 * | party_b    | lender_signer    |
 */
export const LEGACY_SIGNER_ROLE_MAP: Readonly<Record<string, SignerRole>> = {
  party_a: SignerRole.BORROWER,
  party_b: SignerRole.LENDER_SIGNER,
};
